import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "../../components/domain/auth/AuthLayout";
import { LoginForm } from "../../components/domain/auth/LoginForm";
import { useBetaWall } from "../../hooks/useBetaWall";
import { useUserContext } from "../../context/UserContext";
import { asInternalPath } from "../../lib/internal_path";
import { ROUTES } from "../../route_paths";
import { useSignIn } from "./useSignIn";

/**
 * Full-page sign-in. `?redirect=` names where to go afterwards. A signed-in user with no redirect
 * is sent on; with one they may be here to switch account (a guard refused their role), so the
 * form stays. Offers a way back to the app for anyone who would rather not sign in.
 */
export default function SignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, loading } = useUserContext();
  const wallUp = useBetaWall();
  const redirect = asInternalPath(searchParams.get("redirect"));
  const destination = redirect ?? ROUTES.landing.path();
  const {
    email,
    setEmail,
    password,
    setPassword,
    error,
    loading: signingIn,
    handleSubmit,
  } = useSignIn({ onSuccess: () => navigate(destination, { replace: true }) });

  if (loading) return null;
  const isNamed = !!session && !session.user.is_anonymous;
  if (isNamed && !redirect) return <Navigate to={destination} replace />;

  return (
    <AuthLayout>
      <LoginForm
        title={t("auth.signInPage.title")}
        subtitle={t("auth.signInPage.subtitle")}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        loading={signingIn}
        error={error}
        onSubmit={handleSubmit}
      />
      <Stack alignItems="center" gap={0.5}>
        {wallUp ? (
          <Button component={Link} to={ROUTES.joinBeta.path()} variant="text" size="small">
            {t("auth.signInPage.joinBeta")}
          </Button>
        ) : (
          <Button component={Link} to={ROUTES.landing.path()} variant="text" size="small">
            {t("auth.signInPage.backToApp")}
          </Button>
        )}
      </Stack>
    </AuthLayout>
  );
}
